import { useCallback, useMemo } from 'react';

import { TabBarActionButton } from './TabBarActionButton';
import { TabBarItem } from './TabBarItem';

import type { BottomTabBarProps } from './BottomTabBar.types';
import type { NavItem } from '@/types/layout';

import { LAYOUT_DIMENSIONS } from '@/types/layout';

/**
 * Bottom tab bar navigation component for mobile devices.
 *
 * Features:
 * - 64px height + safe area inset at bottom
 * - 5-item layout with equal width distribution
 * - Icon (24px) + label (12px) per tab
 * - Active state with primary color, inactive with text-muted
 * - Always draws when rendered: when it shows is the shell's call
 *   (`PrivateLayout` renders it below 768px, where there is no sidebar).
 *   A breakpoint of its own here once hid it from 640px, leaving an empty
 *   frame and no navigation between 640 and 767px.
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { id: 'home', label: 'Home', icon: <HomeIcon />, href: '/' },
 *   { id: 'plans', label: 'Plans', icon: <PlansIcon />, href: '/plans' },
 * ];
 *
 * <BottomTabBar
 *   items={tabs}
 *   activeItemId="home"
 *   onItemClick={(item) => navigate(item.href)}
 * />
 *
 * // With center action button:
 * <BottomTabBar
 *   items={tabs}
 *   activeItemId="home"
 *   onItemClick={(item) => navigate(item.href)}
 *   actionButton={{
 *     icon: <BoltIcon />,
 *     onClick: () => navigate('/start-workout'),
 *     ariaLabel: "Start Workout"
 *   }}
 * />
 * ```
 */
export function BottomTabBar({
  items,
  activeItemId,
  onItemClick,
  actionButton,
  className = '',
}: BottomTabBarProps) {
  const handleItemClick = useCallback(
    (item: NavItem) => {
      onItemClick?.(item);
    },
    [onItemClick]
  );

  // Split items into left and right groups when action button is present
  const { leftItems, rightItems } = useMemo(() => {
    if (!actionButton) {
      return { leftItems: items, rightItems: [] };
    }
    const midpoint = Math.floor(items.length / 2);
    return {
      leftItems: items.slice(0, midpoint),
      rightItems: items.slice(midpoint),
    };
  }, [items, actionButton]);

  return (
    <nav
      role="tablist"
      aria-label="Main navigation"
      className={`
        fixed bottom-0 left-0 right-0
        bg-contrast-700
        border-t border-stroke-default
        z-40
        ${className}
      `}
      style={{
        height: `calc(${LAYOUT_DIMENSIONS.tabBarHeight}px + env(safe-area-inset-bottom, 0px))`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className="flex h-16 w-full items-stretch"
        style={{ height: `${LAYOUT_DIMENSIONS.tabBarHeight}px` }}
      >
        {actionButton ? (
          <>
            {/* Left tab items */}
            {leftItems.map((item) => (
              <TabBarItem
                key={item.id}
                item={item}
                active={item.id === activeItemId || item.active || false}
                onClick={handleItemClick}
              />
            ))}

            {/* Center action button */}
            <TabBarActionButton
              icon={actionButton.icon}
              onClick={actionButton.onClick}
              ariaLabel={actionButton.ariaLabel}
            />

            {/* Right tab items */}
            {rightItems.map((item) => (
              <TabBarItem
                key={item.id}
                item={item}
                active={item.id === activeItemId || item.active || false}
                onClick={handleItemClick}
              />
            ))}
          </>
        ) : (
          items.map((item) => (
            <TabBarItem
              key={item.id}
              item={item}
              active={item.id === activeItemId || item.active || false}
              onClick={handleItemClick}
            />
          ))
        )}
      </div>
    </nav>
  );
}
